import { ElementRef } from '@angular/core';
import { Params } from '@angular/router';
import { WacUiHostSession } from '@office-start/owl-bootstrapper-hub-service';
import { Observable } from 'rxjs';

import { InstrumentationParams, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { WopiHostData } from '../mru/mru-file-item.model';
import { PerformanceMeasureType } from '../services/performance.service';
import { FileType } from './file-item.model';
import { NewFileInfo, OfficeApplication, RebootOverrideData } from './jsapi.model';

export enum DocumentQueryParams {
    ItemId = 'item',
    DriveId = 'drive',
    DocUrl = 'file',
    ContentUri = 'contenturi',
    NewFile = 'newfile',
    BootId = 'bootid',
    Nav = 'nav'
}

export interface WopiFetchData extends WopiFetchDataBase {
    fetchedDocumentInfo: Promise<DocumentInfo>;
}

export interface WopiFetchDataBase {
    wopiHostData: WopiHostData;
    fetchDiagnostics: WacPerformanceTiming;
}

export interface WacPendingLoadData {
    bootstrapperObservable: Observable<WacLoadFunction>;
    fileNamePromise: Promise<string>;
    documentIdentifierPromise?: Promise<DocumentInfo>;
    isNewDocument?: boolean;
}

export type BootNewDocumentUiHostFunction = () => void;

export type UiHostRouteFunction = (additionalParams?: Params) => void;

export type WacLoadFunction = (data: WacLoadParams) => Observable<WacLoadData>;

export interface UnifiedBootSuccess {
    type: 'success';
}

export interface UnifiedBootError {
    type: 'error';
    errorMessage: string;
    isExpected: boolean;
    cleanUp: (endSession: () => Promise<string>) => void;
}

export type UnifiedBootComplete = UnifiedBootSuccess | UnifiedBootError;

export interface UnifiedOfficeApplication {
    session: WacUiHostSession;
    bootCompleteObservable: Observable<UnifiedBootComplete>;
}

export interface WacLoadData {
    wopiHostData?: WopiHostData;
    officeApplication?: OfficeApplication;
    unifiedOfficeApplication?: UnifiedOfficeApplication;
}

export interface WacLoadParams {
    container: ElementRef;
    flushTime: number;
    loadCorrelationId: string;
}

export const enum WacContentPerformanceMarker {
    DocumentLoadStart = 'WacContent_Document_Load_Start',
    DocumentWopiFetchStart = 'WacContent_Document_Wopi_Fetch_Start',
    DocumentWopiFetchEnd = 'WacContent_Document_Wopi_Fetch_End',
    DocumentBootstrapperLoadStart = 'WacContent_Document_Bootstrapper_Load_Start',
    DocumentBootstrapperLoadEnd = 'WacContent_Document_Bootstrapper_Load_End',
    DocumentInitializeWopiPending = 'WacContent_Document_InitializeWopiPending',
    DocumentHostComponentLoaded = 'WacContent_Document_Component_Loaded',
    DocumentLoadEnd = 'WacContent_Document_Load_End'
}

export const WacPerfDumpEventName = 'WacContent_Document_Boot_Diagnostics';

export interface WacPerformanceTiming {
    startTime?: number;
    endTime?: number;
}

export interface BootAndLoadInfo {
    correlationId: string;
    bootedDocumentInfo: BootedDocumentInfo;
    data?: WacPendingLoadData;
}

export interface BootstrapperScriptInfo {
    bootstrapperUrl: string;
    applicationUrl: string;
}

export interface DocumentIdentifier {
    docId?: string;
    driveId?: string;
    docUrl?: string;
    fileName?: string;
}

export interface DocumentInfo extends DocumentIdentifier {
    fileType: FileType;
    wdOrigin: string;

    fileName?: string;
    bootstrapperUrl?: string;
    newFile?: boolean;
    newFileId?: number;
    templateId?: string;
    applicationUrlParams?: Params;
    rebootOverrideData?: RebootOverrideData;
    newFileInfo?: NewFileInfo;
}

export interface BootedDocumentInfo {
    bootId: number;
    documentInfo: DocumentInfo;
}

export interface UiHostPerformanceInfo {
    clickTimeMarker: PerformanceMeasureType | string;
    instrumentationParams: InstrumentationParams;
}

export type WacHostBootErrorEvent =
    KnownInstrumentationEvents.WacDocumentVroomError
    | KnownInstrumentationEvents.WacDocumentGenericBootError
    | KnownInstrumentationEvents.WacDocumentJsApiError
    | KnownInstrumentationEvents.InitializeWopiPendingFailed
    | KnownInstrumentationEvents.WacDocumentBootstrapperLoadError
    | KnownInstrumentationEvents.WacDocumentAbandon
    | KnownInstrumentationEvents.WacDocumentJsApiTimeout;

export interface WacHostBootError {
    instrumentationEvent: WacHostBootErrorEvent;
    error: any;
}
